// Custom select (F-6): zamknięte pole jak F-5, panel na --surface + --shadow-elevated,
// opcja wybrana z ptaszkiem, obsługa klawiatury (strzałki, Enter, Esc), klik-poza zamyka,
// panel odbija się w górę, gdy pod spodem brak miejsca (W-2).
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { IconChevronDown } from "./icons";

export type SelectOption = { value: string; label: string };
export type SelectGroup = { label: string; options: SelectOption[] };

export function Select({
  value,
  onChange,
  options,
  groups,
  ariaLabel,
  disabled,
  placeholder = "Wybierz...",
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options?: SelectOption[];
  groups?: SelectGroup[];
  ariaLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const labelId = useId();

  const flat = useMemo<SelectOption[]>(
    () => (groups ? groups.flatMap((g) => g.options) : (options ?? [])),
    [groups, options],
  );
  const current = flat.find((o) => o.value === value);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) rootRef.current?.querySelector<HTMLElement>(".select__trigger")?.focus();
  }, []);

  const openList = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) setOpenUp(window.innerHeight - rect.bottom < 260 && rect.top > 260);
    setOpen(true);
    setFocusIndex(Math.max(0, flat.findIndex((o) => o.value === value)));
  }, [flat, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const choose = (v: string) => {
    onChange(v);
    close();
  };

  // Typ strukturalny zamiast React.KeyboardEvent — patrz eslint.config.js, reguła no-undef.
  const onKeyDown = (e: { key: string; preventDefault: () => void }) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      const opt = flat[focusIndex];
      if (opt) choose(opt.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  let renderIndex = -1;
  const renderOption = (opt: SelectOption) => {
    renderIndex += 1;
    const idx = renderIndex;
    return (
      <li
        key={opt.value}
        className={`select__option${idx === focusIndex ? " is-focused" : ""}`}
        role="option"
        aria-selected={opt.value === value}
        onMouseEnter={() => setFocusIndex(idx)}
        onClick={() => choose(opt.value)}
      >
        <span>{opt.label}</span>
        <svg
          className="select__check"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          aria-hidden="true"
        >
          <path d="M5 12l5 5L20 7" />
        </svg>
      </li>
    );
  };

  return (
    <div ref={rootRef} className={`select${open ? " is-open" : ""}${className ? " " + className : ""}`}>
      <button
        type="button"
        className="select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : labelId}
        disabled={disabled}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={onKeyDown}
      >
        <span className="select__value" id={labelId}>
          {current ? current.label : placeholder}
        </span>
        <IconChevronDown className="select__chevron" size={16} />
      </button>
      {open ? (
        <ul
          ref={listRef}
          className="select__list"
          role="listbox"
          aria-label={ariaLabel}
          style={openUp ? { top: "auto", bottom: "calc(100% + 6px)" } : undefined}
          onKeyDown={onKeyDown}
        >
          {groups
            ? groups.map((g) => (
                <li key={g.label} role="presentation">
                  <div className="select__group-label">{g.label}</div>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }} role="presentation">
                    {g.options.map(renderOption)}
                  </ul>
                </li>
              ))
            : (options ?? []).map(renderOption)}
        </ul>
      ) : null}
    </div>
  );
}
